// apps/web/src/components/files-browser/helpers.ts

export function formatSize(bytes: number | null): string {
  if (!bytes) return "--";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export const CATEGORY_COLORS: Record<string, string> = {
  image: "bg-blue-500/15 text-blue-400",
  document: "bg-purple-500/15 text-purple-400",
  video: "bg-pink-500/15 text-pink-400",
  audio: "bg-green-500/15 text-green-400",
  data: "bg-yellow-500/15 text-yellow-400",
  other: "bg-gray-500/15 text-gray-400",
};

// Drive-style colored icon backgrounds
export const CATEGORY_ICON_COLORS: Record<
  string,
  { bg: string; text: string }
> = {
  document: { bg: "bg-blue-500/15", text: "text-blue-500" },
  data: { bg: "bg-emerald-500/15", text: "text-emerald-500" },
  image: { bg: "bg-purple-500/15", text: "text-purple-500" },
  video: { bg: "bg-red-500/15", text: "text-red-500" },
  audio: { bg: "bg-orange-500/15", text: "text-orange-500" },
  other: { bg: "bg-gray-500/15", text: "text-gray-400" },
};

export function generateId(): string {
  return crypto.randomUUID();
}

export function getFileIcon(category: string): string {
  const icons: Record<string, string> = {
    image: "image",
    document: "file-text",
    video: "film",
    audio: "music",
    data: "database",
    other: "file",
  };
  return icons[category] || "file";
}

export function getExtension(filename: string): string {
  const parts = filename.split(".");
  return parts.length > 1 ? parts.pop()!.toLowerCase() : "";
}

const CODE_EXTENSIONS = new Set([
  "js",
  "ts",
  "jsx",
  "tsx",
  "py",
  "rb",
  "go",
  "rs",
  "java",
  "c",
  "cpp",
  "h",
  "css",
  "scss",
  "html",
  "xml",
  "json",
  "yaml",
  "yml",
  "toml",
  "sh",
  "bash",
  "zsh",
  "sql",
  "md",
  "mdx",
  "swift",
  "kt",
  "php",
  "lua",
  "r",
  "csv",
  "tsv",
]);

export function isCodeFile(filename: string): boolean {
  return CODE_EXTENSIONS.has(getExtension(filename));
}

/** Extensions that should show the code icon (excludes md, csv, etc.) */
const CODE_ICON_EXTENSIONS = new Set([
  "js",
  "ts",
  "jsx",
  "tsx",
  "py",
  "rb",
  "go",
  "rs",
  "java",
  "c",
  "cpp",
  "h",
  "css",
  "scss",
  "html",
  "xml",
  "json",
  "yaml",
  "yml",
  "toml",
  "sh",
  "bash",
  "zsh",
  "sql",
  "swift",
  "kt",
  "php",
  "lua",
  "r",
]);

export function isCodeIconFile(filename: string): boolean {
  return CODE_ICON_EXTENSIONS.has(getExtension(filename));
}

export function isPdfFile(filename: string): boolean {
  return getExtension(filename) === "pdf";
}

export function isDocFile(filename: string): boolean {
  const ext = getExtension(filename);
  return ["md", "mdx", "markdown", "doc", "docx", "txt", "rtf", "odt"].includes(
    ext
  );
}

export function stripExtension(filename: string): string {
  const dotIdx = filename.lastIndexOf(".");
  return dotIdx > 0 ? filename.slice(0, dotIdx) : filename;
}

export async function downloadFilesAsZip(
  downloads: { filename: string; downloadUrl: string }[]
): Promise<void> {
  if (downloads.length === 0) return;
  const JSZip = (await import("jszip")).default;
  const zip = new JSZip();

  await Promise.all(
    downloads.map(async (f) => {
      try {
        const res = await fetch(f.downloadUrl);
        if (!res.ok) throw new Error(`Status ${res.status}`);
        const blob = await res.blob();
        zip.file(f.filename, blob);
      } catch (err) {
        console.warn("Failed to fetch file for zip:", f.filename, err);
      }
    })
  );

  const zipBlob = await zip.generateAsync({ type: "blob" });
  const url = URL.createObjectURL(zipBlob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Archive-${downloads.length}-files.zip`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
